import { AvatarSizes, type TeamPledgeDistributionDTO, WithSkeleton } from '@capsicum/common';
import { Image, ListCard, SkeletonItem, Text } from '@capsicum/common-web';
import { useWCPledgeLeaderboardCard } from '@capsicum/features/world-championship-pledge/shared';
import styled from 'styled-components';

export type WCPledgeLeaderboardCardProps = {
	item: TeamPledgeDistributionDTO;
	rank: number;
};

const WCPledgeLeaderboardCardComponent = ({ item, rank }: WCPledgeLeaderboardCardProps) => {
	const { cardColor, flagUrl, isFirstPlace, isTopThree, percentage } = useWCPledgeLeaderboardCard(item, rank);

	return (
		<S.LeaderboardCard
			$backgroundColor={cardColor?.background}
			$borderColor={cardColor?.border}
			$isFirstPlace={isFirstPlace}
			$isTopThree={isTopThree}
			data-testid={`wcpledge-leaderboard-card-${item.team.name}`}
		>
			<S.Container $isFirstPlace={isFirstPlace}>
				<S.RankContainer>
					<Text testID={`wcpledge-leaderboard-card-rank-${rank}`} variant="textTopBar">
						{rank}
					</Text>
				</S.RankContainer>
				<S.StartContainer>
					<S.FlagImageWrapper>
						<Image
							alt={`${item.team.name} flag`}
							data-testid={`wcpledge-leaderboard-card-flag-${item.team.name}`}
							height={AvatarSizes.S}
							src={flagUrl}
							width={AvatarSizes.S}
						/>
					</S.FlagImageWrapper>
				</S.StartContainer>
				<ListCard.Content>
					<ListCard.Row>
						<Text testID={`wcpledge-leaderboard-card-name-${item.team.name}`} variant="textTopBar">
							{item.team.name}
						</Text>
					</ListCard.Row>
				</ListCard.Content>
				<ListCard.End>
					<Text testID={`wcpledge-leaderboard-card-percentage-${item.team.name}`} variant="text8">
						{`${percentage.toFixed(1)}%`}
					</Text>
				</ListCard.End>
			</S.Container>
		</S.LeaderboardCard>
	);
};

export const WCPledgeLeaderboardCardSkeleton = () => (
	<S.LeaderboardCard $isFirstPlace={false} $isTopThree={false}>
		<S.Container $isFirstPlace={false}>
			<S.RankContainer>
				<SkeletonItem borderRadius={2} height={20} width={24} />
			</S.RankContainer>
			<S.StartContainer>
				<S.FlagImageWrapper>
					<ListCard.Image isLoading />
				</S.FlagImageWrapper>
			</S.StartContainer>
			<ListCard.Content>
				<ListCard.Row>
					<SkeletonItem borderRadius={2} height={20} width={108} />
				</ListCard.Row>
			</ListCard.Content>
			<ListCard.End>
				<SkeletonItem borderRadius={2} height={20} width={40} />
			</ListCard.End>
		</S.Container>
	</S.LeaderboardCard>
);

export const WCPledgeLeaderboardCard = WithSkeleton(WCPledgeLeaderboardCardComponent, WCPledgeLeaderboardCardSkeleton);

namespace S {
	export const LeaderboardCard = styled(ListCard)<{
		$backgroundColor?: string;
		$borderColor?: string;
		$isFirstPlace: boolean;
		$isTopThree: boolean;
	}>`
		display: flex;
		flex-direction: column;
		border: ${({ $borderColor }) => ($borderColor ? `2px solid ${$borderColor}` : 'none')};
		background-color: ${({ $backgroundColor, theme }) =>
			$backgroundColor ?? theme.colors.backgrounds.main.container};
	`;

	export const Container = styled.div<{ $isFirstPlace: boolean }>`
		display: flex;
		flex-direction: row;
		width: 100%;
		padding: ${({ $isFirstPlace, theme }) =>
			$isFirstPlace
				? `${theme.spacings.medium} ${theme.spacings.xxsmall}`
				: `${theme.spacings.xsmall} ${theme.spacings.xxsmall}`};
		align-items: center;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const RankContainer = styled.div`
		display: flex;
		align-items: center;
		justify-content: center;
		padding: ${({ theme }) => theme.spacings.small};
	`;

	export const StartContainer = styled(ListCard.Start)`
		padding: 0;
	`;

	export const FlagImageWrapper = styled.div`
		border: 1px solid ${({ theme }) => theme.colors.borders.main.subtle};
		border-radius: 50%;
		padding: 1px;
	`;
}
